import type { SpecialtyBranch } from '@provider-directory/app/ui/sceneFlows';
import {
  isExcluded,
  hasAnswer,
  findLastUnanswered,
  lastOf,
  DECISION_TREE_INITIAL_STEP,
  DecisionTreeSceneSlug,
} from '@provider-directory/models/decision-tree.model';

export interface SharedSnapshot {
  specialtyBranch: SpecialtyBranch | null;
  answers: Record<string, unknown>;
  navigationHistory: string[];
  lastViewedScene: string | null;
  providerId: string | null;
  overrideProviderId?: string | null;
}

export type AnswersUsage = 'skip' | 'prefill' | undefined;

export interface HydrationInput {
  shared: SharedSnapshot;
  specialtyBranch: SpecialtyBranch | null;
  answersUsage?: AnswersUsage;
  currentProviderId?: string | null;
}

export interface HydrationResult {
  isSpecialtyChanged: boolean;
  shouldClearOverride: boolean;
  normalizedHistory: DecisionTreeSceneSlug[];
  resumeScene: DecisionTreeSceneSlug;
}

export interface UseDecisionTreeMemory {
  // base utils
  isExcluded: (slug?: string | null) => boolean;
  hasAnswer: (slug: string, answers: Record<string, unknown>) => boolean;
  findLastUnanswered: (
    history: string[],
    answers: Record<string, unknown>,
  ) => string | null;
  lastOf: <T>(arr: T[]) => T | null;

  // memory helpers
  cleanHistoryTail: (history: string[]) => DecisionTreeSceneSlug[];
  firstUnansweredAfter: (
    history: DecisionTreeSceneSlug[],
    lastViewed: string | null,
    answers: Record<string, unknown>,
  ) => DecisionTreeSceneSlug | null;
  pickSafeResume: (
    candidate: string | null,
    history: DecisionTreeSceneSlug[],
  ) => DecisionTreeSceneSlug;
  normalizeHistory: (
    history: DecisionTreeSceneSlug[],
  ) => DecisionTreeSceneSlug[];
  computeHydration: (input: HydrationInput) => HydrationResult;
}

/**
 * Removes the trailing item if it is excluded (error or disclaimer).
 * Keeps the rest as-is to preserve back/forward semantics.
 */
export function cleanHistoryTail(history: string[]): DecisionTreeSceneSlug[] {
  if (!history.length) return [];
  const tail = lastOf(history);
  if (isExcluded(tail)) return history.slice(0, -1) as DecisionTreeSceneSlug[];
  return history as DecisionTreeSceneSlug[];
}

/**
 * Finds the first unanswered scene strictly AFTER the provided lastViewed,
 * limited to items present in history. Returns null when none found.
 */
export function firstUnansweredAfter(
  history: DecisionTreeSceneSlug[],
  lastViewed: string | null,
  answers: Record<string, unknown>,
): DecisionTreeSceneSlug | null {
  if (!lastViewed) return null;
  const idx = history.indexOf(lastViewed as DecisionTreeSceneSlug);
  if (idx < 0) return null;
  for (let i = idx + 1; i < history.length; i++) {
    const slug = history[i];
    if (!hasAnswer(slug, answers)) return slug;
  }
  return null;
}

/**
 * Picks a safe resume target:
 * - prefer provided candidate if valid and not excluded
 * - else fallback to the last valid item in history
 * - else DECISION_TREE_INITIAL_STEP
 */
export function pickSafeResume(
  candidate: string | null,
  history: DecisionTreeSceneSlug[],
): DecisionTreeSceneSlug {
  if (candidate && !isExcluded(candidate)) {
    return candidate as DecisionTreeSceneSlug;
  }
  const fallback = lastOf(history);
  return fallback || DECISION_TREE_INITIAL_STEP;
}

/**
 * Ensures there is a non-empty, normalized history (defaults to [DECISION_TREE_INITIAL_STEP]).
 */
export function normalizeHistory(
  history: DecisionTreeSceneSlug[],
): DecisionTreeSceneSlug[] {
  if (history.length > 0) return history;
  return [DECISION_TREE_INITIAL_STEP];
}

/**
 * Main hydrator used by the modal on open.
 * - Detects provider changes and cleans up select-time-modal if needed
 * - Cleans the tail if it contains an excluded scene
 * - Computes the resume scene respecting 'skip' vs 'prefill'
 * - Normalizes history and guarantees a valid resume target
 */
export function computeHydration({
  shared,
  specialtyBranch,
  answersUsage,
  currentProviderId,
}: HydrationInput): HydrationResult {
  const isSpecialtyChanged =
    !!shared.specialtyBranch && shared.specialtyBranch !== specialtyBranch;

  const isProviderChanged =
    !!shared.providerId &&
    !!currentProviderId &&
    shared.providerId !== currentProviderId;

  // Detect if we're returning to the original provider after using an override
  // This happens when:
  // 1. There's an overrideProviderId set (we previously switched providers due to RFV mismatch)
  // 2. The current provider matches the original providerId (we're back to the original provider)
  const shouldClearOverride =
    !!shared.overrideProviderId &&
    !!currentProviderId &&
    shared.providerId === currentProviderId;

  // Step 1: Clean up select-time-modal if provider changed OR returning to original provider
  let cleanedHistory = shared.navigationHistory;
  let cleanedLastViewedScene = shared.lastViewedScene;

  if (isProviderChanged || shouldClearOverride) {
    cleanedHistory = shared.navigationHistory.filter(
      (scene) => scene !== 'select-time-modal' && !scene.includes('disclaimer'),
    );
    cleanedLastViewedScene = cleanedHistory.includes(
      shared.lastViewedScene || '',
    )
      ? shared.lastViewedScene
      : lastOf(cleanedHistory);
  }

  // Step 2: history without trailing excluded scenes
  const history = cleanHistoryTail(cleanedHistory);

  // Step 3: choose resume target
  let resume: string | null = null;

  if (answersUsage === 'prefill') {
    resume = DECISION_TREE_INITIAL_STEP;
  } else {
    const unanswered =
      findLastUnanswered(history, shared.answers) ||
      firstUnansweredAfter(history, cleanedLastViewedScene, shared.answers);

    resume =
      unanswered ||
      lastOf(history) ||
      cleanedLastViewedScene ||
      DECISION_TREE_INITIAL_STEP;
  }

  // Step 4: normalize history and pick a safe resume
  const normalizedHistory = normalizeHistory(history);
  const resumeScene = pickSafeResume(resume, normalizedHistory);

  return {
    isSpecialtyChanged,
    shouldClearOverride,
    normalizedHistory,
    resumeScene,
  };
}

export const useDecisionTreeMemory = (): UseDecisionTreeMemory => ({
  isExcluded,
  hasAnswer,
  findLastUnanswered,
  lastOf,
  cleanHistoryTail,
  firstUnansweredAfter,
  pickSafeResume,
  normalizeHistory,
  computeHydration,
});
